// Markdown → sanitized HTML for FAQ answers. Two layers:
//   markdownToHtml  — pure, DOM-free (marked + a link-attr rewrite). Unit-tested.
//   renderFaqMarkdown — markdownToHtml + browser DOMPurify. Client-only (needs a
//                       DOM), verified at runtime like the blog's DOMPurify use.
// DOMPurify is the security boundary: renderFaqMarkdown's output is the ONLY
// string ever passed to dangerouslySetInnerHTML.
import { marked } from "marked";
import DOMPurify from "dompurify";

// DOM-free: markdown → HTML with safe link attributes forced on every anchor.
// This is the pre-sanitization stage and is what the unit tests exercise.
export function markdownToHtml(md: string): string {
  const rawHtml = marked.parse(md ?? "", { async: false }) as string;
  // marked emits `<a href="...">` (no target/rel). Add them exactly once.
  return rawHtml.replace(
    /<a\s+([^>]*?)>/gi,
    (_m, attrs) => `<a ${attrs.trim()} target="_blank" rel="noopener noreferrer">`,
  );
}

// Client-only: render markdown to a sanitized HTML string safe for
// dangerouslySetInnerHTML. Requires a browser DOM (DOMPurify) — never call from
// server/JSON-LD code; use stripMarkdown there instead.
//
// We sanitize to a DOM fragment (RETURN_DOM_FRAGMENT) and then force
// target/rel on anchors ourselves. This is done here — not via a global
// DOMPurify.addHook — so the FAQ renderer does NOT mutate the shared DOMPurify
// singleton and leak its link behavior into other consumers (e.g. the blog).
// The manual pass is also necessary because ALLOWED_URI_REGEXP is applied to
// every URI-ish attribute value (including target="_blank") and would strip
// target/rel if they were added before sanitization.
export function renderFaqMarkdown(md: string): string {
  const html = markdownToHtml(md);
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "strong", "b", "em", "i", "u", "s",
      "ul", "ol", "li", "a", "code", "pre", "blockquote",
      "h3", "h4", "hr",
    ],
    ALLOWED_ATTR: ["href", "target", "rel"],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|\/)/i,
    RETURN_DOM_FRAGMENT: true,
  }) as unknown as DocumentFragment;

  fragment.querySelectorAll("a[href]").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });

  const container = document.createElement("div");
  container.appendChild(fragment);
  return container.innerHTML;
}

// DOM-free plain-text version (for JSON-LD answer text and search snippets).
export function stripMarkdown(md: string): string {
  const rawHtml = marked.parse(md ?? "", { async: false }) as string;
  const noTags = rawHtml.replace(/<[^>]+>/g, " ");
  const decoded = noTags
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return decoded.replace(/\s+/g, " ").trim();
}
